import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { SpinnerComponent } from '../spinner/spinner.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { DeletedRecord, RecycleBinService } from '../../../core/services/recycle-bin.service';
import { ToastService } from '../../../core/services/toast.service';

/**
 * SuperAdmin-only "recycle bin" modal. Reusable across every list: pass the backend `entityKey`
 * and a human `title`; for event sub-data also pass `eventId`. Lists soft-deleted records (scoped
 * server-side to the current tenant + selected BU) with search + pagination, and restores them.
 * Emits `restored` so the host can refresh its own list, and `close` when dismissed.
 */
@Component({
  selector: 'app-recycle-bin-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, SpinnerComponent, ConfirmDialogComponent],
  template: `
    @if (open) {
      <div
        class="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        (click)="onBackdrop()"
      >
        <div
          class="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
          (click)="$event.stopPropagation()"
        >
          <!-- Header -->
          <div class="px-6 pt-5 pb-4 border-b border-gray-100 flex items-start justify-between gap-4">
            <div class="flex items-start gap-3">
              <div class="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 ring-8 ring-blue-50 flex items-center justify-center">
                <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-7 4l1 7m4-7l-1 7M5 7l1 12a2 2 0 002 2h8a2 2 0 002-2l1-12" />
                </svg>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-gray-900 leading-snug">Deleted {{ title }}</h3>
                <p class="text-sm text-gray-500 mt-0.5">Restore records deleted in your current workspace.</p>
              </div>
            </div>
            <button type="button" (click)="onClose()" class="p-1.5 -mr-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Search -->
          <div class="px-6 py-3 border-b border-gray-100">
            <div class="relative">
              <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
              </svg>
              <input
                type="text"
                [(ngModel)]="search"
                (ngModelChange)="searchChanged($event)"
                placeholder="Search deleted {{ title.toLowerCase() }}..."
                class="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500"
              />
            </div>
          </div>

          <!-- Body -->
          <div class="flex-1 overflow-y-auto px-6 py-3 min-h-[200px]">
            @if (loading()) {
              <div class="flex items-center justify-center py-12">
                <app-spinner size="md"></app-spinner>
              </div>
            } @else if (records().length === 0) {
              <div class="flex flex-col items-center justify-center py-12 text-center">
                <svg class="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                    d="M5 13l4 4L19 7M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2" />
                </svg>
                <p class="text-sm font-medium text-gray-700">Nothing here</p>
                <p class="text-xs text-gray-500 mt-1">No deleted {{ title.toLowerCase() }} in your current workspace.</p>
              </div>
            } @else {
              <ul class="divide-y divide-gray-100">
                @for (rec of records(); track rec.id) {
                  <li class="py-3 flex items-start justify-between gap-4">
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-2 flex-wrap">
                        <span class="font-medium text-gray-900 truncate">{{ rec.title }}</span>
                        @if (rec.subtitle) {
                          <span class="text-xs text-gray-500">{{ rec.subtitle }}</span>
                        }
                      </div>
                      @if (rec.meta.length) {
                        <div class="flex items-center gap-1.5 flex-wrap mt-1.5">
                          @for (m of rec.meta; track m.label) {
                            <span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-100 text-[11px] text-gray-600">
                              <span class="text-gray-400">{{ m.label }}:</span> {{ m.value }}
                            </span>
                          }
                        </div>
                      }
                      @if (rec.deletedDate) {
                        <p class="text-[11px] text-gray-400 mt-1">Deleted {{ rec.deletedDate | date: 'medium' }}</p>
                      }
                    </div>
                    <button
                      type="button"
                      (click)="askRestore(rec)"
                      [disabled]="busyId() === rec.id"
                      class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500"
                    >
                      @if (busyId() === rec.id) {
                        <app-spinner size="xs" colour="white"></app-spinner>
                      } @else {
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      }
                      Restore
                    </button>
                  </li>
                }
              </ul>
            }
          </div>

          <!-- Footer / pagination -->
          @if (!loading() && total() > 0) {
            <div class="px-6 py-3 border-t border-gray-100 bg-gray-50 rounded-b-2xl flex items-center justify-between text-sm">
              <span class="text-gray-500">{{ total() }} deleted {{ total() === 1 ? 'record' : 'records' }}</span>
              @if (totalPages() > 1) {
                <div class="flex items-center gap-2">
                  <button type="button" (click)="goTo(page() - 1)" [disabled]="page() <= 1"
                    class="px-2.5 py-1 rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Prev</button>
                  <span class="text-gray-500">{{ page() }} / {{ totalPages() }}</span>
                  <button type="button" (click)="goTo(page() + 1)" [disabled]="page() >= totalPages()"
                    class="px-2.5 py-1 rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
                </div>
              }
            </div>
          }
        </div>
      </div>

      <app-confirm-dialog
        [open]="pendingRestore() !== null"
        title="Restore this {{ title }}?"
        message="This will re-activate"
        [entityName]="pendingRestore()?.title ?? null"
        variant="info"
        confirmLabel="Restore"
        loadingLabel="Restoring..."
        [loading]="busyId() !== null && pendingRestore() !== null"
        (confirm)="confirmRestore()"
        (cancel)="pendingRestore.set(null)"
      ></app-confirm-dialog>
    }
  `,
  styles: [`:host { display: contents; }`],
})
export class RecycleBinModalComponent implements OnChanges, OnDestroy {
  @Input() open = false;
  /** Backend IRecycleBinHandler.Key, e.g. 'members', 'payments', 'event-members'. */
  @Input({ required: true }) entityKey = '';
  /** Plural, human-readable label, e.g. 'Members', 'Payments'. */
  @Input({ required: true }) title = '';
  /** Required for event sub-data (event-members, event-contributions). */
  @Input() eventId: string | null = null;

  /** Emitted after a successful restore so the host can refresh its list. */
  @Output() restored = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  private recycleBin = inject(RecycleBinService);
  private toast = inject(ToastService);

  readonly records = signal<DeletedRecord[]>([]);
  readonly loading = signal(false);
  readonly busyId = signal<string | null>(null);
  readonly total = signal(0);
  readonly page = signal(1);
  readonly pageSize = 20;
  readonly pendingRestore = signal<DeletedRecord | null>(null);

  search = '';
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor() {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.page.set(1);
        this.load();
      });
  }

  totalPages(): number {
    return Math.max(1, Math.ceil(this.total() / this.pageSize));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open) {
      this.search = '';
      this.page.set(1);
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open && this.busyId() === null && this.pendingRestore() === null) this.onClose();
  }

  searchChanged(value: string): void {
    this.searchSubject.next(value);
  }

  goTo(target: number): void {
    if (target < 1 || target > this.totalPages()) return;
    this.page.set(target);
    this.load();
  }

  private load(): void {
    if (!this.entityKey) return;
    this.loading.set(true);
    this.recycleBin
      .getDeleted(this.entityKey, {
        search: this.search || undefined,
        page: this.page(),
        pageSize: this.pageSize,
        eventId: this.eventId ?? undefined,
      })
      .subscribe({
        next: (res) => {
          if (res.success && res.data) {
            this.records.set(res.data.items ?? []);
            this.total.set(res.data.total ?? 0);
          } else {
            this.records.set([]);
            this.total.set(0);
            if (res.message) this.toast.error('Could not load deleted data', res.message);
          }
          this.loading.set(false);
        },
        error: () => {
          this.records.set([]);
          this.total.set(0);
          this.loading.set(false);
          this.toast.error('Could not load deleted data');
        },
      });
  }

  askRestore(rec: DeletedRecord): void {
    this.pendingRestore.set(rec);
  }

  confirmRestore(): void {
    const rec = this.pendingRestore();
    if (!rec) return;
    this.busyId.set(rec.id);
    this.recycleBin.restore(this.entityKey, rec.id, this.eventId ?? undefined).subscribe({
      next: (res) => {
        this.busyId.set(null);
        this.pendingRestore.set(null);
        if (res.success) {
          this.toast.success(`${this.singular()} restored`, rec.title);
          this.records.update((list) => list.filter((r) => r.id !== rec.id));
          this.total.update((t) => Math.max(0, t - 1));
          this.restored.emit();
          if (this.records().length === 0 && this.total() > 0) this.load();
        } else {
          this.toast.error('Restore failed', res.message);
        }
      },
      error: () => {
        this.busyId.set(null);
        this.pendingRestore.set(null);
        this.toast.error('Restore failed');
      },
    });
  }

  onBackdrop(): void {
    if (this.busyId() === null && this.pendingRestore() === null) this.onClose();
  }

  onClose(): void {
    this.close.emit();
  }

  private singular(): string {
    return this.title.endsWith('s') ? this.title.slice(0, -1) : this.title;
  }
}
